import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DataGrid,
  GridEventListener,
  GridColDef,
  GridToolbar,
  GridRenderCellParams,
} from "@mui/x-data-grid";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import Navbar from "../../Components/Navbar";
import { useBranchContext } from "../../Context/BranchContext";
import { IBranch } from "../../@types/branch";
import { StyledButton } from "../../Components/ui/CustomButton";
import WarningIcon from "@mui/icons-material/Warning";
import { Chip, ChipProps } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import CircleIcon from "@mui/icons-material/Circle";
import { formVals } from "./BranchForm/BranchFormUtils";

function getChipProps(params: GridRenderCellParams): ChipProps {
  if (params.value === 0) {
    return {
      icon: <CircleIcon style={{ fill: "#6ac700" }} />,
      label: "Open",
      style: {
        borderColor: "#6ac700",
        color: "#6ac700",
      },
    };
  }
  if (params.value === 1) {
    return {
      icon: <WarningAmberIcon style={{ fill: "orange" }} />,
      label: "Temporarily Closed",
      style: {
        borderColor: "orange",
        color: "orange",
      },
    };
  }
  if (params.value === 2) {
    return {
      icon: <WarningIcon style={{ fill: "red" }} />,
      label: "Closed",
      style: {
        borderColor: "red",
        color: "red",
      },
    };
  } else {
    return {
      icon: <RadioButtonCheckedIcon style={{ fill: "#0076b6" }} />,
      label: "Pending Open",
      style: {
        borderColor: "#0076b6",
        color: "#0076b6",
      },
    };
  }
}

const columns: GridColDef[] = [
  {
    field: "branchStatusId",
    flex: 0.75,
    headerClassName: "header-color",
    headerName: " ",
    renderHeader: () => <strong>{"Status"}</strong>,
    disableExport: true,
    renderCell: (params) => (
      <Chip variant="outlined" {...getChipProps(params)} />
    ),
  },
  {
    field: "branchId",
    flex: 0.75,
    headerClassName: "header-color",
    headerName: "Branch ID",
    renderHeader: () => <strong>{"Branch Id"}</strong>,
    renderCell: (params) => (
      <strong style={{ fontWeight: 1000 }}>{params.value}</strong>
    ),
  },
  {
    field: "city",
    flex: 1,
    headerClassName: "header-color",
    headerName: "City",
    renderHeader: () => <strong>{"City"}</strong>,
  },
  {
    field: "brCode",
    flex: 1,
    headerClassName: "header-color",
    headerName: "Code",
    renderHeader: () => <strong>{"Code"}</strong>,
  },
  {
    field: "manager",
    flex: 1,
    headerClassName: "header-color",
    headerName: "Manager",
    renderHeader: () => <strong>{"Manager"}</strong>,
  },
  {
    field: "mgrEmail",
    flex: 1.5,
    headerClassName: "header-color",
    headerName: "Manager Email",
    renderHeader: () => <strong>{"Manager Email"}</strong>,
  },
  {
    field: "address1",
    flex: 1.5,
    headerClassName: "header-color",
    headerName: "Address",
    renderHeader: () => <strong>{"Address"}</strong>,
    hide: true,
  },
  {
    field: "state",
    flex: 1.5,
    headerClassName: "header-color",
    headerName: "State",
    renderHeader: () => <strong>{"State"}</strong>,
    hide: true,
  },
  {
    field: "zip",
    flex: 1.5,
    headerClassName: "header-color",
    headerName: "Zip",
    renderHeader: () => <strong>{"Zip"}</strong>,
    hide: true,
  },
  {
    field: "phone",
    flex: 1.5,
    headerClassName: "header-color",
    headerName: "Phone",
    renderHeader: () => <strong>{"Phone"}</strong>,
    hide: true,
  },
];

function BranchPage(): JSX.Element {
  const navigate = useNavigate();
  const branchList = useBranchContext();
  const [branches, setBranches] = useState<IBranch[]>([]);

  const routePath = () => {
    navigate("/branch-form", { state: { formVals } });
  };

  const handleEvent: GridEventListener<"rowClick"> = (event) => {
    navigate(`/branch-table/${event.row.branchId}`);
  };

  useEffect(() => {
    if (branchList !== null) setBranches(branchList.branches);
  }, [branchList]);

  return (
    <>
      <Navbar />

      <div className="paper-content">
        <div className="no-padding">
          <h1 className="grid-page-title">Branch Table</h1>
        </div>
        <div
          style={{
            height: 700,
            width: "100%",
            boxShadow: "0 3px 10px rgb(0 0 0 / 0.2)",
          }}
        >
          <DataGrid
            sx={{
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "rgb(245,245,245)",
              },
              width: "100%",
              padding: "2%",
              "& .header-color": {
                color: "#004d84",
                backgroundColor: "rgb(245,245,245)",
              },
            }}
            components={{ Toolbar: GridToolbar }}
            componentsProps={{
              toolbar: {
                csvOptions: { disableToolbarButton: false, allColumns: true },
                printOptions: { disableToolbarButton: true },
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 250 },
              },
            }}
            onRowClick={handleEvent}
            rows={branches || []}
            getRowId={(row) => row.branchId}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10]}
            initialState={{
              sorting: {
                sortModel: [{ field: "branchId", sort: "asc" }],
              },
            }}
          />
        </div>
        <br />
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <StyledButton onClick={routePath} variant="contained">
            {" "}
            Add New Branch {<AddBoxOutlinedIcon />}
          </StyledButton>
        </div>
      </div>
    </>
  );
}

export default BranchPage;
